import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Lead, LeadUpdateRequest } from '../../../core/models/lead.model';
import { ProjectType, TemperatureType } from '../../../core/models/reference-data.model';
import {
  TEMPERATURE_ADD_LEAD_STATUS_PLACEHOLDER,
  isTemperatureTypeUnassigned
} from '../../constants/temperature.constants';
import {
  getTemperatureBadgeBackgroundColor,
  getTemperatureBadgeTextColor,
  getTemperatureDisplayName,
  mapTemperatureTypesToDropdownOptions
} from '../../utils/temperature.util';
import { AioButtonComponent, AioInputFieldComponent, AioMultiSelectComponent, AioSelectComponent, AioSelectValueDirective, AioTagComponent } from '@aio/ui';
import { CustomAttributesViewComponent } from '../custom-attributes-view/custom-attributes-view.component';
import { CustomAttributesFormComponent } from '../custom-attributes-form/custom-attributes-form.component';
import { MultiSelectDropdownComponent } from '../multi-select-dropdown/multi-select-dropdown.component';
import { getCustomColorForLabel } from '../../utils/chip.util';
import { toTitleCaseWords } from '../../utils/string.util';
import { CustomColor } from '../../../core/models/chip.models';
import { MultiSelectOption } from '../../../core/models/select.models';
import { TruncateTooltipDirective } from '../../directives/truncate-tooltip.directive';
import { ScrollThroughDirective } from '../../directives/scroll-through.directive';

@Component({
  selector: 'app-lead-overview-card',
  standalone: true,
  imports: [
    ScrollThroughDirective,
    AioButtonComponent,AioSelectComponent, AioSelectValueDirective, AioMultiSelectComponent, AioInputFieldComponent, CommonModule, FormsModule, AioTagComponent, CustomAttributesViewComponent, CustomAttributesFormComponent, MultiSelectDropdownComponent, TruncateTooltipDirective],
  templateUrl: './lead-overview-card.component.html',
  styleUrls: ['./lead-overview-card.component.scss']
})
export class LeadOverviewCardComponent implements OnChanges {
  @Input() lead?: Lead;
  @Input() projectTypes: ProjectType[] = [];
  @Input() users: any[] = [];
  @Input() leadOrigins: any[] = [];
  @Input() referralChannels: any[] = [];
  @Input() labelTags: any[] = [];
  @Input() temperatureTypes: TemperatureType[] = [];
  @Input() customAttributeDefinitions: any[] = [];
  @Output() editClick = new EventEmitter<void>();
  @Output() expandClick = new EventEmitter<void>();
  @Output() saveClick = new EventEmitter<LeadUpdateRequest>();
  @Output() cancelClick = new EventEmitter<void>();

  isEditing = false;
  isExpanded = false; // Local expand state for the card
  showProjectTypesPopover = false;
  
  @ViewChild(CustomAttributesFormComponent) customAttributesFormComponent?: CustomAttributesFormComponent;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lead'] && !changes['lead'].firstChange && this.isEditing) {
      this.isEditing = false;
      this.isExpanded = false;
    }

    if (changes['users'] && this.isEditing && this.users.length > 0) {
      if (this.editProjectOwnerId && !this.users.some(user => user.userId === this.editProjectOwnerId)) {
        this.initializeProjectOwnerId();
      }
    }
  }

  // Form values
  editTitle = '';
  editProjectTypeIds: number[] = [];
  editProjectValue: number | undefined;
  editTemperatureTypeId: number | null | undefined;
  editProjectOwnerId: number | null = null;
  editDescription = '';
  editReferralChannelId: number | null = null;
  editLeadOriginId: number | null = null;
  editLabelIds: string[] = [];
  editStreet1 = '';
  editStreet2 = '';
  editCity = '';
  editState = '';
  editZip = '';
  editCountry = '';

  // Track if projectValue field was actually modified by the user
  private projectValueModified = false;

  /** Matches first row in `temperatureDropdownOptions`; shown when lead has no status in edit mode. */
  readonly statusPlaceholderLabel = TEMPERATURE_ADD_LEAD_STATUS_PLACEHOLDER;
  readonly originPlaceholderLabel = 'Select lead origin';
  readonly referralPlaceholderLabel = 'Select referral source';

  onEditClick(): void {
    if (!this.isEditing) {
      // Auto-expand the card when editing starts
      if (!this.isExpanded) {
        this.isExpanded = true;
      }
      this.startEditing();
    }
  }

  onExpandClick(): void {
    if (!this.isEditing) {
      this.isExpanded = !this.isExpanded;
      this.expandClick.emit();
    }
  }

  onSaveClick(): void {
    if (!this.lead) return;

    // Only include projectValue if the field was actually modified by the user
    // This prevents sending the field when updating other fields, which causes
    // incorrect history log entries (e.g., "value changed from '0.00' to '0'")
    // When the field is modified, ensure we send the correct numeric value
    // Always send project type as an array of projectTypeIds
    const projectTypeIds = (this.editProjectTypeIds?.length ? this.editProjectTypeIds : this.getProjectTypeIds()) ?? [];
    const updateRequest: LeadUpdateRequest = {
      id: this.lead.id,
      title: this.editTitle,
      projectTypeIds: projectTypeIds.filter(id => id != null && id !== 0),
      // Only include projectValue if the user actually modified this field
      // Convert to number to ensure consistent format and handle 0 correctly
      ...(this.projectValueModified && this.editProjectValue !== undefined && this.editProjectValue !== null && {
        projectValue: Number(this.editProjectValue)
      }),
      // Only include when user chose a real temperature — omit so PUT does not coerce unset → Hot on the server
      ...( !isTemperatureTypeUnassigned(this.editTemperatureTypeId) && {
        temperatureTypeId: Number(this.editTemperatureTypeId)
      }),
      projectOwnerId: this.editProjectOwnerId && this.editProjectOwnerId > 0 ? this.editProjectOwnerId : null,
      description: this.editDescription,
      referralChannelId:
        this.editReferralChannelId != null && this.editReferralChannelId > 0
          ? this.editReferralChannelId
          : undefined,
      leadOriginId:
        this.editLeadOriginId != null && this.editLeadOriginId > 0 ? this.editLeadOriginId : undefined,
      street1: this.editStreet1,
      street2: this.editStreet2.trim() === '' ? null : this.editStreet2,
      city: this.editCity,
      state: this.editState,
      zip: this.editZip,
      countryCode: this.lead.countryCode,
      country: this.editCountry
    };

    // Always include labels in the update request (even if empty array)
    if (this.editLabelIds && this.editLabelIds.length > 0) {
      // Convert selected label IDs to Label objects
      const selectedLabels = this.editLabelIds.map(id => {
        const labelTag = this.labelTags.find(tag => tag.labelTagId.toString() === id);
        if (labelTag) {
          return {
            labelTagId: labelTag.labelTagId,
            name: labelTag.name,
            labelType: labelTag.labelType || '',
            colorCode: labelTag.colorCode || '#808080'
          };
        }
        return null;
      }).filter(label => label !== null) as any[];
      
      (updateRequest as any).labels = selectedLabels;
    } else {
      // Empty array means user removed all labels
      (updateRequest as any).labels = [];
    }

    // Include custom attributes from the form when saving
    if (this.customAttributesFormComponent) {
      const customAttributes = this.customAttributesFormComponent.getFormData();
      if (customAttributes && customAttributes.length > 0) {
        const customAttributesWithEntityId = customAttributes.map(attr => ({
          ...attr,
          entityId: this.lead?.id ?? 0
        }));
        (updateRequest as any).customAttributes = customAttributesWithEntityId;
      }
    }

    this.saveClick.emit(updateRequest);
    // Collapse card and exit edit mode after save
    this.isEditing = false;
    this.isExpanded = false;
  }

  onCancelClick(): void {
    this.isEditing = false;
    this.isExpanded = false; // Collapse card when canceling
    this.cancelClick.emit();
  }

  private initializeProjectOwnerId(): void {
    if (this.lead?.projectOwners && this.lead.projectOwners.length > 0) {
      const projectOwner = this.lead.projectOwners[0];
      const ownerId = projectOwner.userId || projectOwner.id || this.lead?.projectOwnerId;
      if (ownerId && ownerId > 0) {
        this.editProjectOwnerId = ownerId;
        return;
      }
    }

    const projectOwnerId = this.lead?.projectOwnerId;
    if (projectOwnerId && projectOwnerId > 0) {
      this.editProjectOwnerId = projectOwnerId;
      return;
    }

    this.editProjectOwnerId = null;
  }

  /** Map API unset ids (null, undefined, 0) to null so the select shows its placeholder. */
  private normalizeOptionalEntityId(id: number | null | undefined): number | null {
    if (id == null || id <= 0) {
      return null;
    }
    return id;
  }

  private startEditing(): void {
    this.isEditing = true;
    // Reset modification tracking
    this.projectValueModified = false;
    
    // Initialize form values from lead
    this.editTitle = this.lead?.title || '';
    this.editProjectTypeIds = [...this.getProjectTypeIds()];
    this.editProjectValue = this.lead?.projectValue;
    // Normalize unset status to null so the select shows its placeholder
    this.editTemperatureTypeId = this.lead?.temperatureTypeId ?? null;
    
    this.initializeProjectOwnerId();
    
    this.editDescription = this.lead?.description || '';
    this.editReferralChannelId = this.normalizeOptionalEntityId(this.lead?.referralChannelId);
    this.editLeadOriginId = this.normalizeOptionalEntityId(this.lead?.leadOriginId);
    this.editLabelIds = this.getLabelsForForm(this.lead?.labels || []);
    this.editStreet1 = this.lead?.street1 || '';
    this.editStreet2 = this.lead?.street2 ?? '';
    this.editCity = this.lead?.city || '';
    this.editState = this.lead?.state || '';
    this.editZip = this.lead?.zip || '';
    this.editCountry = this.lead?.country || '';
  }
  
  getLabelsForForm(labels: any[]): string[] {
    if (!labels || !Array.isArray(labels)) return [];
    return labels
      .filter(label => label && label.labelTagId !== undefined && label.labelTagId !== null)
      .map(label => label.labelTagId.toString());
  }
  
  get labelSelectOptions(): MultiSelectOption[] {
    return this.labelTags.map(labelTag => ({
      value: labelTag.labelTagId.toString(),
      label: labelTag.name,
      colorCode: labelTag.colorCode
    }));
  }
  
  get referralChannelOptions(): any[] {
    const options = this.referralChannels.map(channel => ({
      label: channel.name,
      value: channel.referralChannelId
    }));
    return options;
  }

  get leadOriginOptions(): any[] {
    const options = this.leadOrigins.map(origin => ({
      label: origin.name,
      value: origin.leadOriginId
    }));
    return options;
  }
  
  getTemperatureFromId(temperatureTypeId: number | string | undefined | null): string {
    return getTemperatureDisplayName(this.temperatureTypes, temperatureTypeId);
  }

  getTemperatureBadgeColor(temperatureTypeId: number | string | undefined | null): string {
    return getTemperatureBadgeBackgroundColor(temperatureTypeId);
  }

  getTemperatureBadgeTextColor(temperatureTypeId: number | string | undefined | null): string {
    return getTemperatureBadgeTextColor(temperatureTypeId);
  }

  /** IDs from backend: projectTypeIds array, or single projectTypeId for backward compatibility. */
  getProjectTypeIds(): number[] {
    const l = this.lead;
    if (!l) return [];
    if (l.projectTypeIds?.length) {
      return l.projectTypeIds.filter(id => id != null && id !== 0);
    }
    if (l.projectTypeId != null && l.projectTypeId !== undefined && l.projectTypeId !== 0) {
      return [l.projectTypeId];
    }
    return [];
  }

  /** Array of project type names for display (one per line on the card). */
  getProjectTypeNameList(): string[] {
    const ids = this.getProjectTypeIds();
    if (!ids.length) return [];
    if (this.lead?.projectTypes?.length) {
      const names = this.lead.projectTypes.map(t => t.name).filter(Boolean);
      if (names.length) return names.map(n => toTitleCaseWords(n));
    }
    return ids
      .map(id => this.getProjectTypeName(id))
      .filter(n => n !== '-' && n !== '')
      .map(n => toTitleCaseWords(n));
  }

  toggleProjectTypesPopover(event: Event): void {
    event.stopPropagation();
    this.showProjectTypesPopover = !this.showProjectTypesPopover;
  }

  @HostListener('document:click')
  closeProjectTypesPopover(): void {
    this.showProjectTypesPopover = false;
  }

  getProjectTypeName(projectTypeId: number | undefined): string {
    if (projectTypeId === undefined || projectTypeId === null || projectTypeId === 0) {
      return '';
    }
    if (!this.projectTypes || this.projectTypes.length === 0) {
      return '';
    }
    const projectType = this.projectTypes.find(pt => pt.projectTypeId === projectTypeId);
    return projectType ? projectType.name : '';
  }

  getProjectOwnerName(): string {
    // Check if we have projectOwners array from the backend response
    if (this.lead?.projectOwners && this.lead.projectOwners.length > 0) {
      const projectOwner = this.lead.projectOwners[0];
      if (projectOwner.firstName || projectOwner.lastName) {
        const fullName = `${projectOwner.firstName || ''} ${projectOwner.lastName || ''}`.trim();
        return fullName || projectOwner.email || 'Unknown User';
      }
    }

    const projectOwnerId = this.lead?.projectOwnerId;
    if (!projectOwnerId || projectOwnerId <= 0) {
      return '-';
    }

    const owner = this.users.find(user => {
      return user.userId && user.userId.toString() === projectOwnerId.toString();
    });

    if (owner) {
      const fullName = `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
      return fullName || owner.email || 'Unknown User';
    }

    return '-';
  }

  getProjectOwnerProfilePicture(): string | null {
    // Check if we have projectOwners array from the backend response
    if (this.lead?.projectOwners && this.lead.projectOwners.length > 0) {
      return this.lead.projectOwners[0].profilePictureUrl || null;
    }

    // Fallback to old projectOwnerId logic
    if (!this.lead?.projectOwnerId) {
      return null;
    }

    // Find the user in the users array by projectOwnerId
    const owner = this.users.find(user => {
      return user.userId && user.userId.toString() === this.lead?.projectOwnerId?.toString();
    });

    return owner?.profilePictureUrl || null;
  }

  getProjectOwnerRole(): string {
    // Check if we have projectOwners array from the backend response
    if (this.lead?.projectOwners && this.lead.projectOwners.length > 0) {
      const projectOwner = this.lead.projectOwners[0];
      return projectOwner.roleName || '-';
    }

    const projectOwnerId = this.lead?.projectOwnerId;
    if (!projectOwnerId || projectOwnerId <= 0) {
      return '-';
    }

    const owner = this.users.find(user => {
      return user.userId && user.userId.toString() === projectOwnerId.toString();
    });

    // Note: UserByPermission might not have roleName, but check anyway
    return (owner as any)?.roleName || '-';
  }

  formatBudget(budget: number | undefined): string {
    if (!budget) {
      return '$0.00';
    }
    return '$' + budget.toLocaleString();
  }

  /** Formatted value for the Value (USD) input: $ and commas, e.g. $50,000. Underlying model stays numeric. */
  getFormattedBudgetDisplay(): string {
    if (this.editProjectValue === undefined || this.editProjectValue === null) {
      return '';
    }
    const num = Number(this.editProjectValue);
    if (isNaN(num)) return '';
    return '$' + Math.floor(num).toLocaleString();
  }

  // Helper method to convert string to number
  toNumber(value: string | number | undefined): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'number') {
      return value;
    }
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  }

  // Helper method to format user name
  getUserDisplayName(user: any): string {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName || user.email || 'Unknown User';
  }

  // Get selected owner user object
  getSelectedOwner(): any | null {
    if (!this.editProjectOwnerId) return null;
    return this.users.find(user => user.userId === this.editProjectOwnerId) || null;
  }

  // Remove selected owner
  removeOwner(): void {
    this.editProjectOwnerId = null;
  }

  get projectTypeMultiSelectOptions(): { label: string; value: number }[] {
    return (this.projectTypes ?? []).map(type => ({
      label: toTitleCaseWords(type.name),
      value: type.projectTypeId
    }));
  }

  getProjectTypeSelectedLabel(
    selectedItem: { label?: string; value?: number } | number | null | undefined
  ): string {
    if (selectedItem == null) {
      return '';
    }
    if (typeof selectedItem === 'object' && selectedItem.label) {
      return selectedItem.label;
    }
    const value = typeof selectedItem === 'object' ? selectedItem.value : selectedItem;
    const option = this.projectTypeMultiSelectOptions.find(o => o.value === value);
    return option?.label ?? String(value ?? '');
  }

  // Convert users to dropdown options
  get userOptions(): any[] {
    const options = this.users.map(user => ({
      label: this.getUserDisplayName(user),
      value: user.userId
    }));
    return options;
  }





  get temperatureDropdownOptions(): any[] {
    const base = mapTemperatureTypesToDropdownOptions(this.temperatureTypes);
    return base;
  }


  /** Integer dollars as `$50,000`. The field applies this on each keystroke. */
  readonly formatDollarInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    return digits ? '$' + Number(digits).toLocaleString() : '';
  };

  // Handle Value (USD) input: display as $ with commas (e.g. $50,000), store as number (e.g. 50000)
  onBudgetInput(value: string): void {
    const digitsOnly = value.replace(/\D/g, '');
    const num = digitsOnly === '' ? undefined : parseInt(digitsOnly, 10);
    this.projectValueModified = true;
    this.editProjectValue = num;
  }

  getReferralSource(): string {
    if (!this.lead?.referralChannelId) return '-';
    const channel = this.referralChannels.find(rc => rc.referralChannelId === this.lead?.referralChannelId);
    return channel?.name || '-';
  }

  getOrigin(): string {
    if (!this.lead?.leadOriginId) return '-';
    const origin = this.leadOrigins.find(lo => lo.leadOriginId === this.lead?.leadOriginId);
    return origin?.name || '-';
  }

  getAddress(): string {
    if (!this.lead) return '-';
    const parts = [
      this.lead.street1,
      this.lead.street2,
      this.lead.city,
      this.lead.state,
      this.lead.zip
    ].filter(part => part && part.trim() !== '');
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  getLabels() {
    return this.lead?.labels || [];
  }

  getLabelColorCode(labelId: number): string {
    const label = this.lead?.labels?.find(l => l.labelTagId === labelId);
    return label?.colorCode || '#808080'; // Default grey
  }

  getLabelName(label: any): string {
    return label.name || '-';
  }

  getCustomColor(label: any): CustomColor {
    return getCustomColorForLabel(label.labelTagId, this.getLabelColorCode.bind(this));
  }

  getGoogleMapsUrl(): string {
    if (!this.lead) return '#';
    const address = this.getAddress();
    if (address === '-') return '#';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
}
